import { useEffect, useRef, useState } from "react";
import { Phone, MessageCircle, FileText } from "lucide-react";
import { Link, useLocation } from "react-router-dom";

export default function MobileBottomBar() {
  const [visible, setVisible] = useState(false);
  const location = useLocation();
  const hideOffer =
    location.pathname.startsWith("/preise") ||
    location.pathname.startsWith("/angebot-angenommen");
  const [chatLoading, setChatLoading] = useState(false);
  const [chatError, setChatError] = useState(false);
  const chatTimer = useRef<ReturnType<typeof setInterval> | null>(null);
  useEffect(
    () => () => {
      if (chatTimer.current) clearInterval(chatTimer.current);
    },
    [],
  );
  const openChat = () => {
    if (chatLoading) return;
    setChatError(false);
    setChatLoading(true);
    window.dispatchEvent(new Event("spyclean:load-chat"));
    const started = Date.now();
    const tryOpen = () => {
      const api = (
        window as Window & {
          leadConnector?: { chatWidget?: { openWidget: () => void } };
        }
      ).leadConnector?.chatWidget;
      if (api?.openWidget) {
        const root = document.querySelector("chat-widget")?.shadowRoot;
        if (root && !root.querySelector("#spyclean-mobile-launcher-style")) {
          const style = document.createElement("style");
          style.id = "spyclean-mobile-launcher-style";
          style.textContent =
            "@media(max-width:1023px){.lc_text-widget--bubble{display:none!important}}";
          root.appendChild(style);
        }
        api.openWidget();
        setChatLoading(false);
        return true;
      }
      if (Date.now() - started > 15000) {
        setChatLoading(false);
        setChatError(true);
        return true;
      }
      return false;
    };
    if (!tryOpen())
      chatTimer.current = setInterval(() => {
        if (tryOpen() && chatTimer.current) {
          clearInterval(chatTimer.current);
          chatTimer.current = null;
        }
      }, 250);
  };
  // On service pages, the Preis action opens the calculator with the
  // service + customer type already preselected.
  const routePreis = (() => {
    if (location.pathname.startsWith("/leistungen/haushaltsreinigung"))
      return "/preise?kundentyp=Privat&service=Haushaltsreinigung";
    if (location.pathname.startsWith("/leistungen/bueroreinigung"))
      return "/preise?kundentyp=Gewerbe&service=B%C3%BCroreinigung";
    if (location.pathname.startsWith("/leistungen/praxisreinigung"))
      return "/preise?kundentyp=Gewerbe&service=Praxisreinigung";
    if (location.pathname.startsWith("/leistungen/treppenhausreinigung"))
      return "/preise?kundentyp=Gewerbe&service=Treppenhausreinigung";
    if (location.pathname.startsWith("/leistungen/grundreinigung"))
      return "/preise?kundentyp=Privat&service=Grundreinigung";
    if (location.pathname.startsWith("/leistungen/fensterreinigung"))
      return "/preise?kundentyp=Privat&service=Fensterreinigung";
    if (location.pathname.startsWith("/leistungen/glasreinigung"))
      return "/preise?kundentyp=Privat&service=Fensterreinigung";
    if (location.pathname.startsWith("/leistungen/buegelservice"))
      return "/preise?kundentyp=Privat&service=Buegelservice";
    if (location.pathname.startsWith("/leistungen/steinreinigung"))
      return "/preise?kundentyp=Privat&service=Steinreinigung";
    if (location.pathname.startsWith("/leistungen/polsterreinigung"))
      return "/preise?kundentyp=Privat&service=Polsterreinigung";
    if (location.pathname.startsWith("/leistungen/baureinigung"))
      return "/preise?kundentyp=Gewerbe&service=Bauendreinigung";
    if (location.pathname.startsWith("/leistungen/umzugreinigung"))
      return "/preise?kundentyp=Privat&service=Umzugreinigung";
    return "/preise";
  })();
  const preisTo = routePreis;

  useEffect(() => {
    const onScroll = () => setVisible(window.scrollY > 250);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  if (!visible && !hideOffer) return null;

  return (
    <div
      aria-label="Schnellkontakt"
      className="fixed bottom-0 left-0 right-0 z-[10000] flex min-h-[58px] w-full bg-[#04346F] text-white lg:hidden border-t border-white/10 transition-transform duration-300"
      style={{ paddingBottom: "env(safe-area-inset-bottom)" }}
    >
      <a
        href="tel:+4917664017221"
        className="flex flex-1 flex-col items-center justify-center gap-1 border-r border-white/20 text-xs font-medium"
      >
        <Phone className="h-5 w-5" />
        <span>Anrufen</span>
      </a>
      <button
        type="button"
        onClick={openChat}
        disabled={chatLoading}
        aria-label="Chat öffnen"
        aria-busy={chatLoading}
        className="flex flex-1 flex-col items-center justify-center gap-1 border-r border-white/20 text-xs font-medium"
      >
        <MessageCircle className="h-5 w-5" />
        <span>{chatLoading ? "Chat lädt …" : "Chat"}</span>
      </button>
      {chatError && (
        <p
          role="status"
          className="absolute bottom-full left-0 right-0 bg-white p-3 text-center text-sm text-primary shadow-lg"
        >
          Chat gerade nicht erreichbar. Bitte versuchen Sie es erneut oder rufen
          Sie uns an.
        </p>
      )}
      {!hideOffer && (
        <Link
          to={preisTo}
          className="home-mobile-offer flex flex-1 flex-col items-center justify-center gap-1 text-xs font-medium"
        >
          <FileText className="h-5 w-5" />
          <span className="text-center leading-tight">Angebot anfordern</span>
        </Link>
      )}
    </div>
  );
}
